// TOEFL iBT Mock 3 — Reading (2026 format, two timed modules)
//
// Task types:
//   words    – Complete the Words: [[word]] marks a word whose second
//              half is hidden; the test taker types the missing letters.
//   daily    – Read in Daily Life: short everyday texts (notices, emails, memos).
//   academic – Read an Academic Passage: short passage, 5 questions
//              (the last question is a "where does this sentence fit?" item;
//              the [A]–[D] markers are part of the passage text).
//
// Everyone takes module1. Scoring >= 60% on it routes to the harder
// "upper" Module 2; otherwise the easier "lower" Module 2.
//
// NOTE: Mock Test 3 in the PDF has a single Module 2, so `upper` and `lower`
// currently point to the same content. Replace `lower` with easier material
// if you want true adaptive routing.

// Each module is timed separately (about 30 minutes in total).
export const MODULE_SECONDS = { module1: 16 * 60, module2: 14 * 60 };

export const TASK_LABELS = {
  words: "Complete the Words",
  daily: "Read in Daily Life",
  academic: "Academic Passage",
};

// ── Module 1 ─────────────────────────────────────────────────
const module1 = [
    {
      id: "m1-words",
      type: "words",
      title: "Bioluminescence",
      text: "Bioluminescence is a fascinating natural phenomenon where living organisms produce and emit light. This remarkable [[ability]] is [[particularly]] common in the deep [[ocean]], where [[sunlight]] cannot [[penetrate]]. Many marine [[creatures]] rely on this glowing adaptation for essential survival [[purposes]], such as [[attracting]] prey or [[evading]] dangerous [[predators]]. For instance, the anglerfish [[dangles]] a bright appendage to draw [[small]] fish [[closer]] to its [[mouth]]. Understanding these unique mechanisms helps biologists learn more about life in extreme underwater habitats.",
    },
    {
      id: "m1-daily1",
      type: "daily",
      title: "Weekend Shuttle Notice",
      text: `Weekend Shuttle Notice: This Saturday, the Blue Line campus shuttle will run every 30 minutes instead of every 15 minutes because two buses are being inspected. Service will begin at 9:00 a.m. and end at 8:00 p.m. The Library stop will be skipped after 5:00 p.m.; riders should use the Student Center stop instead. Regular weekend service is expected to return on Sunday morning.`,
      questions: [
        {
          question: "What is the main purpose of the notice?",
          options: [
            "To announce that the Blue Line is permanently canceled",
            "To ask students to inspect campus buses",
            "To inform riders about temporary Saturday shuttle changes",
            "To introduce a new Sunday shuttle route",
          ],
          correct: 2,
        },
        {
          question: "How often will the Blue Line run this Saturday?",
          options: [
            "Every 15 minutes",
            "Every 30 minutes",
            "Once each hour",
            "Only after 5:00 p.m.",
          ],
          correct: 1,
        },
        {
          question: "What should riders do after 5:00 p.m. if they usually use the Library stop?",
          options: [
            "Wait until Sunday morning",
            "Take a bus being inspected",
            "Use the Library stop as usual",
            "Use the Student Center stop",
          ],
          correct: 3,
        },
      ],
    },
    {
      id: "m1-daily2",
      type: "daily",
      title: "Cedar Court Notice",
      text: `Notice to Residents of Cedar Court Apartments

Date: November 3

The city water department will repair a main pipe on Cedar Street next Tuesday, November 11. As a result, water will be shut off in all buildings from 9:00 a.m. until approximately 2:00 p.m. Hot water may appear cloudy for a few hours after service returns; this is harmless and will clear after you run the tap for several minutes.

We recommend filling containers with water for drinking and cooking before 9:00 a.m. Management will provide bottled water at the front office from 8:30 a.m. to 3:00 p.m. Please do not run the dishwasher or washing machine just before the shutoff. For questions, call the office at 555-0142.`,
      questions: [
        {
          question: "What will management provide during the shutoff?",
          options: [
            "Free meals at the front office",
            "Bottled water at the front office",
            "A temporary kitchen in each building",
            "Reduced rent for November",
          ],
          correct: 1,
        },
        {
          question: "What can be inferred about cloudy hot water after the repair?",
          options: [
            "Residents do not need to worry about it.",
            "It means the pipe was not fixed properly.",
            "It will last for several days.",
            "It is unsafe to use for washing.",
          ],
          correct: 0,
        },
        {
          question: "What are residents advised to do before 9:00 a.m. on November 11?",
          options: [
            "Call the water department",
            "Run the dishwasher",
            "Fill containers with water",
            "Pick up bottled water",
          ],
          correct: 2,
        },
      ],
    },
    {
      id: "m1-academic",
      type: "academic",
      title: "The Guiding Needle: Evolution of the Magnetic Compass",
      text: `The magnetic compass is one of humanity's most transformative navigational tools, yet its origins lie in mysticism rather than maritime exploration. The earliest compasses, developed in China over two thousand years ago during the Han Dynasty, were made of lodestone, a naturally magnetized iron ore. These early devices, often shaped like a spoon resting on a flat bronze plate, were not used to cross oceans but to harmonize buildings and environments according to the principles of geomancy, or feng shui. They were valued for their perceived ability to align human endeavors with the invisible forces of the Earth.

It was not until the Song Dynasty (960–1279 C.E.) that the compass was adapted for maritime navigation. Sailors discovered that by magnetizing an iron needle and floating it in a bowl of water, they could reliably determine true south, even when clouds obscured the sun and stars. This innovation revolutionized sea travel, allowing ships to venture farther from coastlines into open waters. [A] The technology gradually spread westward along the Silk Road and maritime trade routes. [B] By the 12th century, the compass had reached Europe and the Islamic world, where it underwent further refinement. [C] The "dry compass," which mounted a magnetized needle on a pivot rather than floating it in a liquid, became the standard in Europe. [D]

The widespread adoption of the compass fundamentally altered the course of global history. During the Age of Discovery in the 15th and 16th centuries, European navigators relied heavily on the dry compass to chart unfamiliar oceans, leading to the establishment of transcontinental trade networks. Beyond commerce, the compass spurred advancements in cartography, as mapmakers could finally plot coastlines and ocean currents with unprecedented accuracy. Today, while satellite-based GPS has largely superseded the magnetic compass in modern navigation, the simple magnetized needle remains an enduring symbol of human ingenuity.`,
      questions: [
        {
          question: "The word \"obscured\" in the passage is closest in meaning to",
          options: [
            "hid",
            "damaged",
            "replaced",
            "illuminated",
          ],
          correct: 0,
        },
        {
          question: "According to paragraph 1, what was the primary purpose of the earliest compasses?",
          options: [
            "To navigate trade ships across the ocean",
            "To locate deposits of naturally magnetized iron ore",
            "To align structures with the natural forces of the Earth",
            "To predict the movement of stars in the night sky",
          ],
          correct: 2,
        },
        {
          question: "What can be inferred from paragraph 2 about maritime navigation before the Song Dynasty?",
          options: [
            "Sailors relied primarily on the dry compass for long voyages.",
            "Navigators depended heavily on visible celestial bodies to determine their direction.",
            "Ships were unable to carry iron needles due to the risk of rusting.",
            "Trade routes were entirely restricted to overland travel such as the Silk Road.",
          ],
          correct: 1,
        },
        {
          question: "Where would the following sentence best fit in paragraph 2? \"This design improvement made the instrument much more practical for use on the unsteady decks of rolling ships.\"",
          options: [
            "Choice A",
            "Choice B",
            "Choice C",
            "Choice D",
          ],
          correct: 3,
        },
        {
          question: "Why does the author mention \"transcontinental trade networks\" in paragraph 3?",
          options: [
            "To emphasize the lack of overland trade routes during the Age of Discovery",
            "To illustrate one of the major historical impacts of the magnetic compass",
            "To argue that cartography was the most profitable scientific field of the era",
            "To explain why the dry compass was originally invented in Europe",
          ],
          correct: 1,
        },
      ],
    },
];

// ── Module 2 ─────────────────────────────────────────────────
const module2 = [
    {
      id: "m2-words",
      type: "words",
      title: "Exoplanets",
      text: "Astronomers have discovered thousands of planets orbiting stars other than our Sun, which are known as exoplanets. [[One]] common [[detection]] method [[relies]] on watching a [[star]] for [[tiny]] dips in [[brightness]]. When a [[planet]] passes in [[front]] of its [[star]], it blocks a [[small]] fraction of [[the]] light [[that]] reaches [[our]] telescopes. By [[measuring]] how [[often]] the [[dimming]] repeats, researchers can calculate how long the planet takes to complete one orbit. The depth of the dip also reveals the approximate size of the planet compared with its star. Combined with other observations, these results help scientists judge whether a distant world might have conditions suitable for liquid water.",
    },
    {
      id: "m2-daily1",
      type: "daily",
      title: "Library Book Sale",
      text: `Greenfield Public Library

Friends of the Library Annual Used Book Sale

Saturday, April 19, 9:00 a.m. to 4:00 p.m.

Sunday, April 20, 12:00 noon to 3:00 p.m. ($5 fill-a-bag special)

Location: Community Room, 210 Lakeview Drive

Hardcovers $2, paperbacks $1, children's books 50 cents. Cash and cards accepted. All proceeds fund the library's summer reading program. Members of the Friends group may shop early on Friday, April 18, from 5:00 to 7:00 p.m.; memberships are available at the door for $15.

Volunteers are still needed to sort donations on Thursday evening. Parking is available behind the library. Please bring your own bags, as only a limited number will be provided.`,
      questions: [
        {
          question: "Who may shop on Friday evening?",
          options: [
            "Anyone who brings a bag",
            "Volunteers who sorted books",
            "Children under twelve",
            "Members of the Friends group",
          ],
          correct: 3,
        },
        {
          question: "In the flyer, the word \"proceeds\" is closest in meaning to",
          options: [
            "money earned from the sale",
            "books donated to the library",
            "people who attend the event",
            "prices set for each item",
          ],
          correct: 0,
        },
        {
          question: "What is the main purpose of the flyer?",
          options: [
            "To ask for donations of children's books",
            "To announce a change in library hours",
            "To promote a book sale that supports a library program",
            "To recruit new library employees",
          ],
          correct: 2,
        },
      ],
    },
    {
      id: "m2-daily2",
      type: "daily",
      title: "Order Update",
      text: `Order Update from BrightHome Online

Due to unusually high demand for our Aurora standing fans, orders placed between July 1 and July 10 will ship 5 to 7 business days later than the estimate shown at checkout. Orders containing other items will not be affected.

If you prefer not to wait, you may cancel your order for a full refund from the Order History page until it ships. After that, standard return rules apply, and the 30-day return window begins on the delivery date. Customers who keep their orders will automatically receive a $10 credit toward a future purchase. Credits expire on December 31. We apologize for the inconvenience and thank you for your patience.`,
      questions: [
        {
          question: "What is the main purpose of the notice?",
          options: [
            "To announce a sale on standing fans",
            "To notify customers of a delay affecting certain orders",
            "To explain how to return a damaged fan",
            "To introduce a new Order History page",
          ],
          correct: 1,
        },
        {
          question: "What will customers who keep their delayed orders receive?",
          options: [
            "A $10 credit for a future purchase",
            "A full refund",
            "A free standing fan",
            "Free shipping for one year",
          ],
          correct: 0,
        },
        {
          question: "In the notice, the word \"standard\" is closest in meaning to",
          options: [
            "simple",
            "high-quality",
            "temporary",
            "usual",
          ],
          correct: 3,
        },
      ],
    },
    {
      id: "m2-academic",
      type: "academic",
      title: "Behavioral Economics and Human Choice",
      text: `Classical economic theory assumes that individuals act as rational agents who weigh costs and benefits consistently in order to maximize their own welfare. Beginning in the 1970s, however, psychologists Daniel Kahneman and Amos Tversky documented systematic ways in which real human decisions depart from this ideal. Their work helped establish behavioral economics, a field that combines insights from psychology with economic analysis to explain how people actually choose.

A central concept in this field is loss aversion, the tendency for losses to feel more painful than equivalent gains feel pleasurable. [A] In experiments, many people refuse a coin toss that offers an equal chance of winning or losing the same sum of money, even though the bet is statistically fair. [B] Loss aversion also helps explain why investors often hold declining stocks for too long, since selling would turn a paper loss into a realized one.

Another well-documented bias is anchoring, in which an initial piece of information disproportionately influences later judgments. Shoppers who first see a high original price tend to perceive a discounted price as a bargain, even when it exceeds the item's ordinary value. [C] Negotiators, too, are affected, since the first figure mentioned in a bargaining session often shapes the final agreement.

These findings have influenced public policy. Rather than forbidding choices or imposing penalties, policymakers increasingly use "nudges," subtle changes in how options are presented that steer people toward beneficial outcomes. For example, enrolling employees in a retirement savings plan automatically, while allowing them to opt out, substantially raises participation compared with requiring them to sign up. [D] Critics caution, however, that nudges can be used to manipulate as well as to help, and they argue that the goals behind them deserve public scrutiny.`,
      questions: [
        {
          question: "The word \"steer\" in the passage is closest in meaning to",
          options: [
            "guide",
            "delay",
            "restrict",
            "surprise",
          ],
          correct: 0,
        },
        {
          question: "According to paragraph 2, loss aversion helps explain which behavior of investors?",
          options: [
            "Refusing to invest in any stock market",
            "Selling profitable stocks too early",
            "Choosing stocks by tossing a coin",
            "Keeping declining stocks longer than they should",
          ],
          correct: 3,
        },
        {
          question: "What can be inferred from paragraph 3 about retailers who display a high original price beside a sale price?",
          options: [
            "They are required by law to show the original price.",
            "They may be making the sale price seem more attractive.",
            "They usually sell items below their ordinary value.",
            "They want shoppers to ignore the sale price.",
          ],
          correct: 1,
        },
        {
          question: "According to the passage, all of the following are true EXCEPT:",
          options: [
            "Behavioral economics draws on psychology.",
            "Kahneman and Tversky began their work in the 1970s.",
            "Nudges work by forbidding certain choices.",
            "First figures in a negotiation can influence the outcome.",
          ],
          correct: 2,
        },
        {
          question: "Where would the following sentence best fit? \"This occurs because many people simply stay with the default option rather than make an active decision.\"",
          options: [
            "[A]",
            "[B]",
            "[C]",
            "[D]",
          ],
          correct: 3,
        },
      ],
    },
];

export const readingModules = {
  module1,
  upper: module2,
  lower: module2,
};

// Splits "[[word]]" markers into { shown, missing } segments.
// The first half (rounded up) of each word is shown; the rest is typed.
export function parseWordsText(text) {
  const parts = [];
  const re = /\[\[([^\]]+)\]\]/g;
  let last = 0;
  let match;
  let blank = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) parts.push({ kind: "text", value: text.slice(last, match.index) });
    const word = match[1];
    const keep = Math.ceil(word.length / 2);
    parts.push({ kind: "blank", index: blank++, word, shown: word.slice(0, keep), missing: word.slice(keep) });
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push({ kind: "text", value: text.slice(last) });
  return parts;
}